import React from "react";
import type { FC } from "react";
import UtilityPanel from "../layout/UtilityPanel";
import CorePanelFooter from "../layout/CorePanelFooter";
import CoreLayout from "../layout/CoreLayout";
import { IconCaution } from "../icons";
import Button from "../form/Button";
import { useAppStore } from "../../store/appStore";
import type { UserAccount } from "../../api/dev-data/getSessionStatus";
import { jumpToSessionStatus } from "../../api/api";
import AccountItem from "../components/AccountItem";
import { withErrorBoundary } from "../components/withErrorBoundary";

interface Props {
    utilityResponse: {
        alreadyAuthorizedAccounts: UserAccount[];
        failedToAuthorizeAccounts: UserAccount[];
    };
    cancelSession: () => void;
    getSessionId: () => string | null;
}

const AuthzError: FC<Props> = (props) => {
    const { setForceCheckSessionStatus } = useAppStore();

    function onNext() {
        const sessionId = props.getSessionId();
        if (sessionId == null) {
            console.error("Session ID is null");
            return;
        }
        jumpToSessionStatus(sessionId, "AUTHZ_WAITING");
        setForceCheckSessionStatus(true);
    }

    const header = (
        <div className="self-stretch inline-flex justify-start items-center gap-3">
            <IconCaution />
            <div className="flex-1 justify-start font-normal font-['Inter']">
                <div className="text-king-red text-base">
                    We were unable to authorize the following accounts.
                </div>
                <div className="text-king-red text-xs font-normal">
                    An error occurred. Please try again later.
                </div>
            </div>
        </div>
    );

    return (
        <CoreLayout>
            <UtilityPanel headerOverride={header}>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        We're sorry for the inconvenience. PG&E may be
                        experiencing intermittent issues right now. If the
                        issues persist, please try again later.
                    </div>
                    {props.utilityResponse.failedToAuthorizeAccounts.length >
                        0 && (
                        <div className="flex flex-col gap-2">
                            The following accounts were not authorized:
                            <ul className="list-disc ml-4">
                                {props.utilityResponse.failedToAuthorizeAccounts.map(
                                    (account) => (
                                        <li key={account.utilityAccountNumber}>
                                            <AccountItem account={account} />
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    )}
                    {props.utilityResponse.alreadyAuthorizedAccounts.length >
                        0 && (
                        <div className="flex flex-col gap-2">
                            The following accounts were already correctly authorized, so we took no action:
                            <ul className="list-disc ml-4">
                                {props.utilityResponse.alreadyAuthorizedAccounts.map(
                                    (account) => (
                                        <li key={account.utilityAccountNumber}>
                                            <AccountItem account={account} />
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    )}

                    <div className="w-full flex justify-end">
                        <Button onClick={onNext} type="primary">
                            Try Again
                        </Button>
                    </div>
                </div>
            </UtilityPanel>
            <CorePanelFooter>
                <Button
                    onClick={props.cancelSession}
                    type="primary"
                    subType="outline"
                >
                    Cancel
                </Button>
            </CorePanelFooter>
        </CoreLayout>
    );
};

export default withErrorBoundary(AuthzError);
